import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, timeout } from 'rxjs/operators';
import { GlobalSearchService } from './global-search.service';
import {
  GLOBAL_SEARCH_SCOPE_OPTIONS,
  GlobalSearchHit,
  GlobalSearchScope
} from './global-search.types';

@Injectable({ providedIn: 'root' })
export class GlobalSearchController {
  private globalSearchService = inject(GlobalSearchService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly scopeOptions = GLOBAL_SEARCH_SCOPE_OPTIONS;
  readonly suggestions = signal<GlobalSearchHit[]>([]);
  readonly loading = signal(false);
  readonly term = signal<string | unknown>('');
  readonly scope = signal<GlobalSearchScope>('all');

  readonly scopeOption = computed(
    () => this.scopeOptions.find((o) => o.value === this.scope()) ?? this.scopeOptions[0]
  );
  readonly scopeLabel = computed(() => this.scopeOption().label);
  readonly scopeIcon = computed(() => this.scopeOption().icon);
  readonly scopeMenuItems = computed<MenuItem[]>(() => {
    const current = this.scope();
    return this.scopeOptions.map((opt) => ({
      label: opt.label,
      icon: opt.icon,
      styleClass:
        current === opt.value
          ? 'navbar-search-scope-menu__item navbar-search-scope-menu__item--active'
          : 'navbar-search-scope-menu__item',
      command: () => this.changeScope(opt.value)
    }));
  });

  private allResults = signal<GlobalSearchHit[]>([]);
  private query$ = new Subject<string>();

  constructor() {
    this.query$
      .pipe(
        debounceTime(1000),
        switchMap((q) => {
          if (q.length < 3) {
            return of<GlobalSearchHit[]>([]);
          }
          return this.globalSearchService.search(q).pipe(
            timeout({ first: 30_000 }),
            map((rows) =>
              rows.map((r) => ({
                ...r,
                displayLine:
                  r.title?.trim() ||
                  r.description?.trim() ||
                  [r.entityType, r.entityId].filter(Boolean).join(' #') ||
                  'Sin descripción'
              }))
            ),
            catchError(() => of<GlobalSearchHit[]>([]))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((items) => {
        this.allResults.set(items);
        this.suggestions.set(this.filterByScope(items, this.scope()));
        this.loading.set(false);
      });
  }

  complete(event: { query: string }): void {
    const q = (event.query ?? '').trim();
    if (q.length < 3) {
      this.loading.set(false);
      this.allResults.set([]);
      this.suggestions.set([]);
      return;
    }
    this.loading.set(true);
    this.query$.next(q);
  }

  changeScope(nextScope: GlobalSearchScope): void {
    this.scope.set(nextScope);
    this.suggestions.set(this.filterByScope(this.allResults(), nextScope));
  }

  select(value: unknown, onNavigate?: () => void): void {
    if (!value || typeof value !== 'object') {
      return;
    }
    const hit = value as GlobalSearchHit;
    const path = hit.path?.trim();
    if (path) {
      const url = path.startsWith('/') ? path : `/${path}`;
      void this.router.navigateByUrl(url);
    } else {
      const route = this.routeForEntity(hit.entityType, hit.entityId);
      if (route) {
        void this.router.navigate(route);
      }
    }
    this.clear();
    onNavigate?.();
  }

  clear(): void {
    this.term.set('');
    this.loading.set(false);
    this.allResults.set([]);
    this.suggestions.set([]);
  }

  navIconClass(icon: string | null | undefined, entityType: string | null | undefined): string {
    const raw = icon?.trim();
    if (!raw) {
      return this.entityIconClass(entityType);
    }
    if (raw.includes('pi pi')) {
      return raw;
    }
    if (raw.startsWith('pi-')) {
      return `pi ${raw}`;
    }
    return raw;
  }

  entityIconClass(entityType: string | null | undefined): string {
    const t = (entityType ?? '').toLowerCase();
    if (t === 'invoice') return 'pi pi-file';
    if (t === 'customer') return 'pi pi-users';
    if (t === 'product') return 'pi pi-box';
    if (t === 'charge') return 'pi pi-credit-card';
    if (t === 'user') return 'pi pi-user';
    if (t === 'paymentlink' || t === 'payment_link') return 'pi pi-link';
    if (t === 'subscription') return 'pi pi-sync';
    if (t === 'sale') return 'pi pi-shopping-cart';
    if (t === 'salelink') return 'pi pi-link';
    if (t === 'plan') return 'pi pi-list';
    return 'pi pi-search';
  }

  private filterByScope(items: GlobalSearchHit[], nextScope: GlobalSearchScope): GlobalSearchHit[] {
    if (nextScope === 'all') {
      return items;
    }
    const target = nextScope.toLowerCase();
    return items.filter((item) => (item.entityType ?? '').toLowerCase() === target);
  }

  private routeForEntity(
    entityType: string | null | undefined,
    entityId: string | null | undefined
  ): string[] | null {
    if (!entityId) {
      return null;
    }
    const t = (entityType ?? '').toLowerCase();
    switch (t) {
      case 'invoice':
        return ['/invoices', entityId];
      case 'customer':
        return ['/customers', entityId];
      case 'product':
        return ['/products', entityId];
      case 'user':
        return ['/users', entityId];
      case 'charge':
        return ['/collections'];
      case 'sale':
        return ['/sales', entityId];
      case 'salelink':
        return ['/sale-links', entityId];
      case 'subscription':
        return ['/subscriptions', entityId];
      case 'plan':
        return ['/plans', entityId];
      default:
        return null;
    }
  }
}
